import { Util } from "./Util";
import { Source } from "./Source";
import { List } from "./List";
import { Input } from "./Input";
import { InputCollection } from "./InputCollection";
import { PolyList } from "./PolyList";
import { Mesh } from "./Mesh";
import { Geometry } from "./Geometry";
import { ImageResource } from "./ImageResource";
import { Effect } from "./Effect";
import { Material } from "./Material";
import { ObjectContainer } from "./ObjectContainer";
import { Controller } from "./Controller";
import { Skin } from "./Skin";
import { VertexWeightContainer } from "./VertexWeightContainer";
import { Scene } from "./Scene";
import { SceneNode } from "./SceneNode";
import { Animation } from "./Animation";
import { Channel } from "./Channel";
import { Sampler } from "./Sampler";
import { AnimationState } from "./AnimationState";
import { AnimationClip } from "./AnimationClip";
import * as pako from 'pako';

export class Loader {

    private blobs: { [key: string]: ColladaBlob };
    public util: Util;
    public colladaObjects: { [key: string]: ObjectContainer };

    constructor() {
        this.blobs = {};
        this.colladaObjects = {};
    }

    public processData(data: ArrayBuffer) {
        let infl = pako.inflate(data);
        let dataView = new DataView(infl.buffer);
        let offset = 0;

        let header = new Uint8Array(dataView.buffer, 0, 8);
        offset += 8;

        if (!this.validateHeader(header)) {
            throw new Error("Invalid SPI-COLLADA file");
        }

        this.util = new Util((identity) => this.getBlob(identity));

        while ((offset = this.readBlobHeader(dataView, offset)) < dataView.byteLength) {
            // Do nothing
        }

        return this.colladaObjects;
    }

    public getBlob<T extends ColladaBlob>(blobIdentity: number): T {
        let identity = '' + blobIdentity;
        return this.blobs[identity] as T;
    }

    private registerBlob(blob: ColladaBlob, blobIdentity: number) {
        let identity = '' + blobIdentity;
        if (!this.blobs[identity]) {
            this.blobs[identity] = blob;
        }
    }

    private validateHeader(header: Uint8Array) {
        // Verify header is %SPI____
        return header[0] === 0x25 && header[1] === 0x53 && header[2] === 0x50 && header[3] === 0x49;
    }

    private readBlobHeader(dataView: DataView, offset: number): number {

        let blobType = dataView.getUint16(offset);
        offset += 2;
        let blobIdentity = dataView.getInt32(offset, true);
        offset += 4;
        let blobLength = dataView.getInt32(offset, true);
        offset += 4;

        let blob: ColladaBlob = null;
        switch (blobType) {
            case 0x5F53:
                blob = new Source();
                break;
            case 0x5F4C:
                blob = new List();
                break;
            case 0x5F49:
                blob = new Input();
                break;
            case 0x4349:
                blob = new InputCollection();
                break;
            case 0x4C50:
                blob = new PolyList();
                break;
            case 0x5F4D:
                blob = new Mesh();
                break;
            case 0x5F47:
                blob = new Geometry();
                break;
            case 0x5249:
                blob = new ImageResource(`img_${blobIdentity}`);
                break;
            case 0x4645:
                blob = new Effect();
                break;
            case 0x544D:
                blob = new Material(`material_${blobIdentity}`);
                break;
            case 0x434f:
                blob = new ObjectContainer();
                break;
            case 0x4f43:
                blob = new Controller();
                break;
            case 0x4b53:
                blob = new Skin();
                break;
            case 0x5756:
                blob = new VertexWeightContainer();
                break;
            case 0x5356:
                blob = new Scene();
                break;
            case 0x4e53:
                blob = new SceneNode();
                break;
            case 0x4e41:
                blob = new Animation();
                break;
            case 0x4843:
                blob = new Channel();
                break;
            case 0x4153:
                blob = new Sampler();
                break;
            case 0x5341:
                blob = new AnimationState();
                break;
            case 0x5641:
                blob = new AnimationClip();
                break;
        }

        if (blob) {
            blob.loadBlob(new DataView(dataView.buffer, offset, blobLength), this);
            this.registerBlob(blob, blobIdentity);
        } else {
            console.warn(`BlobType ${blobType} is unhandled (identity ${blobIdentity}, length ${blobLength}`);
        }

        offset += blobLength;

        return offset;
    }
}

export type ColladaBlob = {
    loadBlob: (dataView: DataView, loader: Loader) => void;
}

